import { CartItem } from '../models/cart.model';
import { PosPricingRule } from '../models/pos-catalog.model';
import { toCents } from '../../shared/utils/money.utils';
import type { PosPricedItem } from './pos-data.service';

/**
 * Applique la réponse de /pos/calculate aux lignes du panier : règles
 * applicables, prix, total exact de ligne et règle BOGO à afficher.
 *
 * Fonction pure, sortie de `CartService.doRefreshPricing` (limite de 500
 * lignes). Les lignes sont associées au prix par POSITION, comme le backend
 * les renvoie.
 */
export function appliquerReponseCalcul(
    items: CartItem[],
    pricedItems: PosPricedItem[],
    rulesMap: Record<string, PosPricingRule[]>,
): CartItem[] {
    return items.map((item, index) => {
        const pricedItem = pricedItems[index];
        const rules: PosPricingRule[] = rulesMap[item.id] || [];
        let selected = item.selectedRule;

        // If selected rule no longer applicable, clear it
        if (selected && !rules.find(r => r.id === selected!.id)) selected = null;

        let updatedItem = { ...item, applicableRules: rules, selectedRule: selected };

        if (pricedItem && String(pricedItem.productId).toLowerCase() === String(item.id).toLowerCase()) {
            // Only fall back to base price if user explicitly cleared a rule AND backend returned no applied rule
            const basePrice = item.packaging.salePrice || (item.unitPrice * item.packaging.multiplier);
            const hasExplicitlyClearedRule = item.selectedRule === null && !pricedItem.appliedRule;
            const priceToUse = hasExplicitlyClearedRule ? basePrice : pricedItem.finalPrice;

            // Track BOGO rule info for display (free items shown as non-editable sub-rows)
            let appliedBogoRule: CartItem['appliedBogoRule'] = null;
            const appliedRule = pricedItem.appliedRule;
            if (appliedRule?.discountStrategy === 'BOGO') {
                const fullRule = rules.find(r => r.name === appliedRule.name && r.discountStrategy === 'BOGO');
                if (fullRule) {
                    appliedBogoRule = {
                        name: fullRule.name,
                        buyQuantity: Number(fullRule.buyQuantity) || 1,
                        getQuantity: Number(fullRule.getQuantity) || 1,
                    };
                }
            }

            // Le backend renvoie le total de ligne exact ; ne pas le
            // reconstituer depuis priceToUse (moyenne arrondie).
            // Si l'utilisateur a explicitement retiré la règle, on
            // repart du prix de base : lineTotal ne s'applique plus.
            const lineTotalCents = hasExplicitlyClearedRule
                ? toCents(basePrice) * item.qty
                : typeof pricedItem.lineTotal === 'number'
                    ? toCents(pricedItem.lineTotal)
                    : undefined;

            updatedItem = {
                ...updatedItem,
                price: priceToUse,
                lineTotalCents,
                appliedBogoRule,
            };
        }
        return updatedItem;
    });
}
